import React, { useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  EmptyState,
  failedEmptyState,
  useIsMobile,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import { ROUTES, trialStartedPath } from "../../../routes/paths";
import BillingDetailsForm, { controlId } from "../components/BillingDetailsForm";
import CheckoutSkeleton from "../components/CheckoutSkeleton";
import PaymentMethods from "../components/PaymentMethods";
import PinnedCtaBar from "../components/PinnedCtaBar";
import TrialLayout from "../components/TrialLayout";
import TrialSummary from "../components/TrialSummary";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { useBillingForm } from "../hooks/useBillingForm";
import { usePlans } from "../hooks/usePlans";
import { useQuote } from "../hooks/useQuote";
import { TRIAL_ACCESS_QUERY_KEY } from "../hooks/useTrialAccess";
import { startTrial } from "../services/trialService";
import type { TrialAccess } from "../types/subscriptions.types";
import {
  cycleParam,
  stashTrial,
  trialBillingOf,
  trialEndOf,
} from "../trialState";

/**
 * The second step of the trial: who the plan will be billed to when the
 * fourteen days are up.
 *
 * ## Why a form at all, when nothing is being charged
 *
 * Because something will be. The trial ends on a date this screen names, and
 * the invoice raised that day has to be headed with a name, an address and a
 * place of supply — so the details are collected once, now, while the person is
 * already here, rather than by interrupting them a fortnight later. It is the
 * same eight fields the paid checkout asks for, filled from the same two
 * records, through the same hook: see `useBillingForm`.
 *
 * What is *not* asked for is a card. There is no `buySubscription` on this
 * screen and no gateway window — the summary's "Due today" is ₹0, and pressing
 * the button records the billing profile and nothing else.
 *
 * ## What starting a trial actually does
 *
 * Two calls, in this order and no other:
 *
 *   1. `PATCH /billing/profile/` — who the invoice at the end of the fortnight
 *      will be headed with. A refusal stops the press.
 *   2. `POST /billing/trial/` — the trial itself. It answers with the dates the
 *      *server* set, and those are what the confirmation reports.
 *
 * The order is the paid checkout's, for the same reason: a trial started
 * against details the server never accepted is a charge nobody can be billed
 * for. And nothing is confirmed that was not started — a refusal at step two
 * keeps the reader on this screen with what they typed.
 *
 * The fortnight is the backend's from that point on. `useSubscriptionGate` asks
 * it what is running and gets `access_source: "FREE_TRIAL"` back, which is what
 * actually opens the product.
 *
 * The figures are the quote's, exactly as on the paid checkout: this screen
 * works none of them out, so what somebody is told they will pay in a fortnight
 * is what the backend would charge for that plan today.
 */
const TrialDetailsPage: React.FC = () => {
  const { cycle } = useParams<{ cycle: string }>();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const getToken = useTokenGetter();
  // The one cache a started trial makes wrong: see `handleStart`.
  const queryClient = useQueryClient();
  // This step is laid out twice rather than restyled, the same way the paid
  // checkout is — see the two branches at the foot of this file.
  const isMobile = useIsMobile();

  // What is on sale, off the backend's own price list — the same call the plan
  // screen this page was opened from drew itself with, so what is promised here
  // is what was offered there.
  const priceList = usePlans();
  const plan = priceList.plans.find((candidate) => candidate.id === cycle);

  // Who this workspace is invoiced, and the brand it falls back to. Called
  // before the early returns below can be reached — hooks cannot be skipped.
  const form = useBillingForm();

  // What the plan will come to when the trial ends, off the backend.
  const quote = useQuote(plan?.code, plan?.id);
  const price = quote.price;

  /**
   * The three loads this screen opens on, as one wait: the plan, what it will
   * come to, and the details it fills itself from.
   *
   * One wait because the screen is one screen. The form does not need the price
   * and could be drawn the moment it mounts, but a form arriving first and a
   * summary dropping in beside it a moment later reads as a page that broke and
   * then repaired itself — and it moves the thing somebody is about to press.
   * So both columns are held until every answer is in and then painted
   * together.
   *
   * While it runs the page shows the shape of itself, as the paid checkout
   * does — the same `CheckoutSkeleton`, in the same two columns. It draws
   * nothing at all for the first moment: `useLoadPhase` holds the placeholder
   * back until the wait is long enough to notice, so a fast answer opens
   * straight onto the real screen and only a slow one is given a shape to look
   * at. An empty white page for a second is the one version of this that reads
   * as broken.
   */
  const opening =
    form.loading ||
    priceList.status === "loading" ||
    quote.status === "loading";
  const phase = useLoadPhase(opening);
  const pricingFailed =
    priceList.status === "failed" || quote.status === "failed";

  const [starting, setStarting] = useState(false);
  /**
   * **No coupon on this step, and that is deliberate.**
   *
   * This screen used to carry the paid checkout's coupon row — a field, a
   * "View coupons" dialog and a line on the card — on the argument that a
   * code somebody was given on the way in should not have to be found again
   * when they come to subscribe. That argument belonged to the mandate: the
   * trial converted itself into a subscription, and a code put on the order
   * here would have met the charge at the end of the fortnight.
   *
   * Prepaid ended that. `POST /billing/trial/` takes no body, nothing on this
   * side writes the code anywhere that outlives the page, and the trial
   * converts into nothing — it ends, Pro pauses, and the account waits on Free
   * until somebody buys a term. So a code entered here was accepted, drawn on
   * the card, and silently dropped on Activate. A field that takes an answer
   * and throws it away is worse than no field: it tells somebody holding a
   * referral code that it has been counted.
   *
   * The code is applied where it can actually be honoured — the paid
   * checkout, which keeps the row, the dialog and all of it. See
   * `BillingCheckoutPage`.
   */

  // The last day the fortnight covers — today counts as the first, so a trial
  // taken on the 21st runs through the 3rd; see `trialState`.
  //
  // Dated once, when the screen opens, rather than at the moment the button is
  // pressed: the summary promises two dates, and the trial that starts has to
  // be the trial that was described. A tab left open over midnight would
  // otherwise confirm a day later than what it showed.
  const [endsOn] = useState(() => trialEndOf());

  // A cycle no plan answers to — a URL typed by hand, or a cycle that has
  // stopped being sold. Sent back to the offer rather than shown an empty form.
  // Only once the list is actually known, since until then every cycle looks
  // like one nothing answers to.
  if (!cycleParam(cycle)) return <Navigate to={ROUTES.trial} replace />;
  if (priceList.status === "ready" && !plan) {
    return <Navigate to={ROUTES.trial} replace />;
  }

  // The one thing on this screen that cannot be drawn from nothing. Nothing has
  // been typed yet — the form is not up — so there is nothing to preserve
  // behind it, and the retry is the whole of what this page can offer.
  if (pricingFailed) {
    return (
      <TrialLayout width="full">
        <EmptyState
          {...failedEmptyState(
            // One message for both, because they are one thing to the reader:
            // the plan and what it will cost are not two facts they asked for
            // separately. Which of the two calls failed is in the console.
            "We could not price this plan",
            <Button
              size="sm"
              variant="secondary"
              onClick={
                priceList.status === "failed" ? priceList.reload : quote.reload
              }
            >
              Try again
            </Button>
          )}
        />
      </TrialLayout>
    );
  }

  // Still opening, or opened onto nothing to price. The shape of the screen
  // stands in — and only once the wait is long enough to be worth standing in
  // for; see `phase`. The next render has both columns at once.
  if (opening || !plan || !price) {
    return (
      <TrialLayout width="full">
        {/* Said as soon as the requests go out, whether or not anything is
            drawn for it: the blocks that go up behind it are the shape of an
            answer rather than the answer, and are hidden from screen
            readers. */}
        <p role="status" className="sr-only">
          Loading your details…
        </p>

        {/* The page's own two columns, so what replaces these blocks stands
            exactly where they were. */}
        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_400px] 3xl:gap-6 4xl:lg:grid-cols-[minmax(0,1fr)_460px]">
          <CheckoutSkeleton phase={phase} />
        </div>
      </TrialLayout>
    );
  }

  /**
   * Starting it.
   *
   * Validated here rather than by disabling the button: a greyed-out Start says
   * nothing about which of eight fields is in the way. So it stays pressable,
   * every field is marked visited at once, and the first one that fails is
   * focused — the same handling the paid checkout gives a failed Pay.
   *
   * The details go up before the trial is confirmed, and a refusal stops it.
   * They are what the invoice at the end of the fortnight will be headed with,
   * and a trial started against details the server never accepted is a charge
   * nobody can be billed for.
   */
  const handleStart = async () => {
    const firstFailing = form.firstFailing;

    if (firstFailing) {
      // Everything at once: a form submitted whole should show everything wrong
      // with it, not the next problem one at a time.
      form.showAllErrors();
      document.getElementById(controlId(firstFailing))?.focus();
      toast.error(
        "Check your billing details",
        "Some of what an invoice needs is missing or does not look right."
      );
      return;
    }

    setStarting(true);
    try {
      const stored = await form.save(form.details);
      // What came back rather than what went up: the server trims and
      // normalises, and the form should end on what the invoice will carry.
      form.reset(stored);
    } catch (cause) {
      setStarting(false);
      toast.error(
        "Could not save your billing details",
        cause instanceof Error
          ? cause.message
          : "We could not reach the server. Please try again."
      );
      console.error("billing profile failed to save:", cause);
      return;
    }

    /**
     * The trial itself, on the backend: `POST /billing/trial/`.
     *
     * Second, and only once the details have been accepted — the order is the
     * paid checkout's, for the same reason. What comes back is the trial the
     * server started, and *its* dates are what the confirmation reports: the
     * two the summary above has been promising were this side's arithmetic, a
     * fair guess at what the server would say and no more than that.
     *
     * A refusal stops the flow here. Nobody is shown a confirmation of a trial
     * that was not started — that is the one screen in this flow that must not
     * be able to lie.
     */
    try {
      const trial = await startTrial(getToken);

      /**
       * The answer `GET /billing/subscription/` is about to give, given to it
       * now — because the dashboard's own guard reads it, and until this it
       * still held the answer from *before* the trial existed.
       *
       * `OfferTrialFirst` asks `canStartTrial` on `/dashboard` and sends a
       * workspace that can still take a fortnight to the offer. Both it and
       * this page read that field through one TanStack Query key, which had
       * `true` in it from the moment the offer was drawn. So pressing Get
       * started went: dashboard, straight back out to the price list — which
       * renders in full off that same cached `true` — and only once its own
       * background read came back `false` was the reader returned to the
       * dashboard they had already asked for. The plan page twice, and the
       * app's first screen arriving third.
       *
       * Writing the new truth here closes that door before anybody walks
       * through it: the guard finds `canStartTrial: false` on arrival and
       * renders the dashboard, with no detour and no spinner in its place.
       * This is not the browser deciding policy — the server has just told us
       * the trial started, and these four fields are the same answer in the
       * shape the cache holds it. It is still only a head start: the query is
       * stale the moment it is written, so `TrialCheck`'s own read refreshes
       * it behind the dashboard either way.
       */
      queryClient.setQueryData<TrialAccess>(
        TRIAL_ACCESS_QUERY_KEY,
        (current: TrialAccess | undefined) => ({
          // Whatever was on the record stays on it — this call started a trial
          // and says nothing about a subscription.
          subscription: current?.subscription ?? null,
          trial,
          // What is holding the door open from here, and what the server will
          // answer for a workspace whose trial is running.
          accessSource: "FREE_TRIAL",
          canStartTrial: false,
        })
      );

      stashTrial({
        cycle: plan.id,
        startedOn: (trial.startedOn ?? new Date()).toISOString(),
        // Dated by the server, whose `ends_at` is the instant the fortnight
        // stops — the morning after the last day it covers, which is the day
        // of the first charge. The fallback is the estimate the summary drew,
        // said in those same terms, for a response that somehow carries a
        // start and no end: a confirmation without a date on it is worse than
        // one carrying the date it just promised.
        endsOn: (trial.endsOn ?? trialBillingOf(endsOn)).toISOString(),
      });
      navigate(trialStartedPath(plan.id));
    } catch (cause) {
      setStarting(false);
      toast.error(
        "Could not start your free trial",
        cause instanceof Error
          ? cause.message
          : "We could not reach the server. Please try again."
      );
      console.error("trial failed to start:", cause);
    }
  };

  /**
   * Putting a code on the order.
   *
   * The dialog closes only where the code actually held — a refusal leaves it
   * open with the reason on the row behind it, which is where somebody is
   * looking. The same handler the paid checkout uses, for the same reasons.
   */
  /**
   * Back to the offer, to start differently.
   *
   * One step back where there is a step to take — this page was opened from
   * the offer a moment ago, and pushing a second copy of it would leave the
   * offer both behind and in front of this step. A page opened cold has
   * nothing behind it, and `location.key` is `"default"` for exactly that
   * first entry; then it is an ordinary trip to the offer. The same shape as
   * the paid checkout's Edit.
   */
  const handleEdit = () => {
    if (location.key === "default") {
      navigate(ROUTES.trial);
      return;
    }
    navigate(-1);
  };

  /**
   * Who the plan will be billed to, with the note that stands in when the
   * saved details could not be read back.
   *
   * Named rather than written into both branches below: the two layouts
   * differ in where this block sits, not in what it is.
   */
  const detailsBlock = (
    <div>
      {form.loadFailed && (
        // Not a failure worth blocking on: these are the buyer's own details,
        // and the form works perfectly well filled in by hand. It says why it
        // is empty so nobody thinks the app has forgotten them.
        <p className="mb-3 rounded-md border border-line bg-[#FAFBFC] px-4 py-3 text-[13px] 3xl:text-[14px] text-ink-muted">
          We could not load your saved details, so the form below is empty.
          Filling it in here works just the same.
        </p>
      )}
      <BillingDetailsForm
        details={form.details}
        onChange={form.change}
        errors={form.errors}
        touched={form.touched}
        onVisit={form.visit}
        busy={starting}
      />
    </div>
  );

  // The page rather than a column of it: this step is a screenful of fields,
  // and a form held to the width the offer was drawn at leaves a band of empty
  // page down either side of it.
  return (
    <TrialLayout width="full">
      {isMobile ? (
        /* The phone's version of this step, in the paid checkout's order and
           for the paid checkout's reason: what is being agreed to comes
           first, the details that go on the invoice second.

           It matters more here, if anything. Nothing is being charged today,
           and the card is the only thing on the page that says so — three
           zeroes and three dates. Put under eight fields, it asked somebody
           to type their address before showing them that the fortnight costs
           nothing, which is the one fact that makes the typing worth it. */
        <div className="flex flex-col gap-4">
          <TrialSummary
            plan={plan}
            price={price}
            endsOn={endsOn}
            onStart={handleStart}
            onEdit={handleEdit}
            starting={starting}
            // The button is pinned across the foot of the screen here rather
            // than drawn inside the card — and the line under it goes with
            // it. See `PinnedCtaBar` at the end of this column.
            payable={false}
          />

          {detailsBlock}

          {/* Nothing is taken today, and these are still worth showing: what
              happens if somebody subscribes is a charge, and who would take
              it is a thing they are entitled to know before agreeing to a
              date. */}
          <PaymentMethods />

          {/* Held across the foot of the screen, as Pay is on the paid
              checkout and for the same reason: this step is eight fields
              long, and the one thing there is to press on it should not be
              something somebody has to reach the bottom of the page to find.
              The classes are `TrialLayout`'s gutter cancelled so the blur runs
              the full width and put back so the button still lines up, and its
              last padding taken off — the shell leaves none under the main on
              a phone — so the bar reaches the bottom of the screen. See
              `PinnedCtaBar`. */}
          <PinnedCtaBar className="-mx-page px-page -mb-page sm:-mx-12 sm:px-12 sm:-mb-10">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              loading={starting}
              onClick={handleStart}
            >
              Activate free trial
            </Button>

            {/* Under the button rather than over it, where the design has it:
                it answers the question the button raises, not one it is
                about to. */}
            <p className="mt-2 text-center text-[12px] text-ink-subtle">
              No payment detail required
            </p>
          </PinnedCtaBar>
        </div>
      ) : (
        /* The form takes what is left; the summary keeps a fixed column, since
           a list of figures does not get better for being wider. The same two
           columns the paid checkout stands in, so the trial and the purchase
           are recognisably the same step of the same product. */
        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_400px] 3xl:gap-6 4xl:lg:grid-cols-[minmax(0,1fr)_460px]">
          {detailsBlock}

          {/* Dropped by the height of a form caption, so the summary card
              starts on the line the form's first card does — the same 40px,
              for the same reason, as on the paid checkout. */}
          <div className="flex flex-col gap-4 lg:mt-10">
            <TrialSummary
              plan={plan}
              price={price}
              endsOn={endsOn}
              onStart={handleStart}
              onEdit={handleEdit}
              starting={starting}
            />

            <PaymentMethods />
          </div>
        </div>
      )}
    </TrialLayout>
  );
};

export default TrialDetailsPage;
